import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Copy, Download, ArrowUp, ArrowDown, Wand2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { api, API } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMPTY = { name: '', towns: '', zips: '', delivery_day: '', delivery_window: '', status: 'Waitlist', min_customer_threshold: 1 };
const STATUS_COLOR = { Active: 'bg-forest/10 text-forest', 'Opening Soon': 'bg-gold/15 text-gold-dark', Waitlist: 'bg-dark/10 text-dark/60' };

const toForm = (r) => ({ ...r, towns: (r.towns || []).join(', '), zips: (r.zips || []).join(', ') });
const toPayload = (f) => ({
  name: f.name, delivery_day: f.delivery_day, delivery_window: f.delivery_window,
  status: f.status, min_customer_threshold: parseInt(f.min_customer_threshold, 10) || 1,
  towns: f.towns.split(',').map((t) => t.trim()).filter(Boolean),
  zips: f.zips.split(',').map((t) => t.trim()).filter(Boolean),
});

export default function AdminRoutes() {
  const [routes, setRoutes] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [customersFor, setCustomersFor] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [moveTarget, setMoveTarget] = useState('');

  const load = useCallback(async () => {
    const r = await api.get('/admin/delivery/routes');
    setRoutes(r.data);
    setAllRoutes(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setForm(EMPTY); };
  const openEdit = (r) => { setEditingId(r.route_id); setForm(toForm(r)); };

  const save = async () => {
    const payload = toPayload(form);
    if (editingId) await api.put(`/admin/delivery/routes/${editingId}`, payload);
    else await api.post('/admin/delivery/routes', payload);
    toast.success(editingId ? 'Route updated' : 'Route created');
    setForm(null);
    load();
  };

  const setStatus = async (routeId, status) => {
    await api.post(`/admin/delivery/routes/${routeId}/status`, { status });
    toast.success(`Route set to ${status}`);
    load();
  };

  const duplicate = async (routeId) => {
    await api.post(`/admin/delivery/routes/${routeId}/duplicate`);
    toast.success('Route duplicated as a template');
    load();
  };

  const remove = async (routeId) => {
    await api.delete(`/admin/delivery/routes/${routeId}`);
    toast.success('Route deleted');
    load();
  };

  const openCustomers = async (route) => {
    setCustomersFor(route);
    const res = await api.get(`/admin/delivery/routes/${route.route_id}/customers`);
    setCustomers(res.data);
  };

  const suggestOrder = async () => {
    await api.post(`/admin/delivery/routes/${customersFor.route_id}/suggest-order`);
    toast.success('Suggested stop order applied');
    openCustomers(customersFor);
  };

  const moveStop = async (orderId, dir) => {
    const ids = customers.active_customers.map((o) => o.order_id);
    const idx = ids.indexOf(orderId);
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    await api.put(`/admin/delivery/routes/${customersFor.route_id}/reorder`, { order_ids: ids });
    openCustomers(customersFor);
  };

  const moveCustomer = async (waitlistId, orderId) => {
    if (!moveTarget) { toast.error('Choose a target route'); return; }
    await api.post('/admin/delivery/routes/move-customer', { waitlist_id: waitlistId, order_id: orderId, target_route_id: moveTarget });
    toast.success('Customer moved');
    openCustomers(customersFor);
    load();
  };

  const setDeliveryStatus = async (orderId, status) => {
    await api.put(`/admin/orders/${orderId}/delivery-status`, { status });
    toast.success(status === 'out_for_delivery' ? 'Customer notified: out for delivery' : status === 'delivered' ? 'Customer notified: delivered' : 'Admin alerted about delivery problem');
    openCustomers(customersFor);
  };

  return (
    <div data-testid="admin-routes">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-manrope text-dark/60 max-w-xl">Geographic delivery routes and zones, starting from Berlin, NH. A town becomes active once it hits your customer threshold.</p>
        <button onClick={openNew} className="btn-primary py-2.5 px-5 text-sm whitespace-nowrap" data-testid="new-route-btn"><Plus className="w-4 h-4 mr-1" />Add Route</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {routes.map((r) => (
          <div key={r.route_id} className="bg-white rounded-2xl border border-forest/10 p-6" data-testid={`route-card-${r.route_id}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-playfair text-xl font-bold text-dark">{r.name}</h3>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_COLOR[r.status]}`} data-testid={`route-status-${r.route_id}`}>{r.status}</span>
            </div>
            <p className="font-manrope text-sm text-dark/60 mb-1">{(r.towns || []).join(', ') || 'No towns assigned yet'}</p>
            <p className="font-manrope text-sm text-dark/60 mb-3">{r.delivery_day ? `${r.delivery_day} · ${r.delivery_window}` : 'No delivery day assigned yet'}</p>
            <div className="flex gap-4 mb-4 font-manrope text-sm">
              <span data-testid={`active-count-${r.route_id}`}><strong className="text-forest">{r.active_customer_count}</strong> active customers</span>
              <span data-testid={`waitlist-count-${r.route_id}`}><strong className="text-gold-dark">{r.waitlist_count}</strong> waitlisted</span>
              <span className="text-dark/40">Threshold: {r.min_customer_threshold}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {r.status !== 'Active' && <button onClick={() => setStatus(r.route_id, 'Active')} className="btn-primary py-1.5 px-3 text-xs" data-testid={`activate-${r.route_id}`}>Activate</button>}
              {r.status !== 'Opening Soon' && <button onClick={() => setStatus(r.route_id, 'Opening Soon')} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`opening-soon-${r.route_id}`}>Set Opening Soon</button>}
              {r.status !== 'Waitlist' && <button onClick={() => setStatus(r.route_id, 'Waitlist')} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`waitlist-${r.route_id}`}>Set Waitlist</button>}
              <button onClick={() => openCustomers(r)} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`view-customers-${r.route_id}`}><Users className="w-3.5 h-3.5 mr-1" />Customers</button>
              <button onClick={() => openEdit(r)} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`edit-route-${r.route_id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</button>
              <button onClick={() => duplicate(r.route_id)} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`duplicate-route-${r.route_id}`}><Copy className="w-3.5 h-3.5 mr-1" />Save as Template</button>
              <a href={`${API}/admin/delivery/routes/${r.route_id}/export.csv`} className="btn-secondary py-1.5 px-3 text-xs" data-testid={`export-route-${r.route_id}`}><Download className="w-3.5 h-3.5 mr-1" />Export CSV</a>
              <button onClick={() => remove(r.route_id)} className="p-1.5 text-dark/40 hover:text-red-500" data-testid={`delete-route-${r.route_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit route */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="bg-cream max-w-lg">
          {form && (
            <>
              <DialogHeader><DialogTitle className="font-playfair text-2xl">{editingId ? 'Edit Route' : 'New Route'}</DialogTitle><DialogDescription>Assign towns/ZIPs, delivery day and window, and activation threshold.</DialogDescription></DialogHeader>
              <div className="space-y-3 font-manrope text-sm">
                <div><Label>Route name</Label><Input className="bg-white border-forest/20" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="route-name" /></div>
                <div><Label>Towns (comma-separated)</Label><Input className="bg-white border-forest/20" value={form.towns} onChange={(e) => setForm({ ...form, towns: e.target.value })} placeholder="Berlin, Gorham" data-testid="route-towns" /></div>
                <div><Label>ZIP codes (comma-separated, optional)</Label><Input className="bg-white border-forest/20" value={form.zips} onChange={(e) => setForm({ ...form, zips: e.target.value })} data-testid="route-zips" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Delivery day</Label><Input className="bg-white border-forest/20" value={form.delivery_day} onChange={(e) => setForm({ ...form, delivery_day: e.target.value })} placeholder="Friday" data-testid="route-day" /></div>
                  <div><Label>Delivery window</Label><Input className="bg-white border-forest/20" value={form.delivery_window} onChange={(e) => setForm({ ...form, delivery_window: e.target.value })} placeholder="8:00 AM–5:00 PM" data-testid="route-window" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Status</Label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="bg-white border border-forest/20 rounded-xl px-3 py-2 w-full" data-testid="route-status-select">
                      <option value="Active">Active</option>
                      <option value="Opening Soon">Opening Soon</option>
                      <option value="Waitlist">Waitlist</option>
                    </select>
                  </div>
                  <div><Label>Customer threshold</Label><Input type="number" className="bg-white border-forest/20" value={form.min_customer_threshold} onChange={(e) => setForm({ ...form, min_customer_threshold: e.target.value })} data-testid="route-threshold" /></div>
                </div>
                <button onClick={save} className="btn-primary py-2 px-5 text-sm" data-testid="save-route">Save Route</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Customers dialog */}
      <Dialog open={!!customersFor} onOpenChange={(v) => !v && setCustomersFor(null)}>
        <DialogContent className="bg-cream max-w-2xl max-h-[80vh] overflow-y-auto">
          {customersFor && customers && (
            <>
              <DialogHeader><DialogTitle className="font-playfair text-2xl">{customersFor.name}</DialogTitle><DialogDescription>Active customers and waitlisted households for this route.</DialogDescription></DialogHeader>
              <div className="font-manrope text-sm space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-dark">Active Customers ({customers.active_customers.length})</h4>
                    <button onClick={suggestOrder} className="btn-secondary py-1.5 px-3 text-xs" data-testid="suggest-order-btn"><Wand2 className="w-3.5 h-3.5 mr-1" />Suggest Stop Order</button>
                  </div>
                  {customers.active_customers.length === 0 ? <p className="text-dark/40">No active customers yet.</p> : (
                    <div className="space-y-2">
                      {customers.active_customers.map((o, idx) => (
                        <div key={o.order_id} className="flex items-center justify-between bg-white rounded-xl p-3 gap-2 flex-wrap" data-testid={`stop-${o.order_id}`}>
                          <span>{idx + 1}. {o.delivery_address?.name} — {o.delivery_address?.street}, {o.delivery_address?.city}
                            <span className="block text-xs text-dark/40">{o.delivery_status || 'confirmed'}</span>
                          </span>
                          <div className="flex gap-1 items-center">
                            <button onClick={() => moveStop(o.order_id, -1)} className="p-1.5 hover:bg-cream rounded" data-testid={`move-up-${o.order_id}`}><ArrowUp className="w-4 h-4" /></button>
                            <button onClick={() => moveStop(o.order_id, 1)} className="p-1.5 hover:bg-cream rounded" data-testid={`move-down-${o.order_id}`}><ArrowDown className="w-4 h-4" /></button>
                            <button onClick={() => setDeliveryStatus(o.order_id, 'out_for_delivery')} className="btn-secondary py-1 px-2 text-xs" data-testid={`out-for-delivery-${o.order_id}`}>Out for Delivery</button>
                            <button onClick={() => setDeliveryStatus(o.order_id, 'delivered')} className="btn-primary py-1 px-2 text-xs" data-testid={`delivered-${o.order_id}`}>Delivered</button>
                            <button onClick={() => setDeliveryStatus(o.order_id, 'delivery_problem')} className="py-1 px-2 text-xs rounded-full border border-red-200 text-red-600 font-manrope font-bold hover:bg-red-50" data-testid={`delivery-problem-${o.order_id}`}>Problem</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <h4 className="font-bold text-dark mb-2">Waitlist ({customers.waitlist.length})</h4>
                  {customers.waitlist.length === 0 ? <p className="text-dark/40">No one waitlisted here yet.</p> : (
                    <div className="space-y-2">
                      {customers.waitlist.map((w) => (
                        <div key={w.waitlist_id} className="flex items-center justify-between bg-white rounded-xl p-3 gap-2" data-testid={`waitlist-entry-${w.waitlist_id}`}>
                          <span>{w.name} — {w.town} ({w.email})</span>
                          <div className="flex items-center gap-2">
                            <select onChange={(e) => setMoveTarget(e.target.value)} defaultValue="" className="bg-cream border border-forest/20 rounded-lg px-2 py-1 text-xs" data-testid={`move-target-${w.waitlist_id}`}>
                              <option value="">Move to route...</option>
                              {allRoutes.filter((r) => r.route_id !== customersFor.route_id).map((r) => <option key={r.route_id} value={r.route_id}>{r.name}</option>)}
                            </select>
                            <button onClick={() => moveCustomer(w.waitlist_id, null)} className="btn-secondary py-1 px-2 text-xs" data-testid={`move-btn-${w.waitlist_id}`}>Move</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
